/**
 * Home hero entrance, "Quiet confidence": the headline, the copy, and the button stay still; the
 * quotes rise into place one after another and the benefit checks tick in. Once the entrance is
 * over, the button arrow nudges right at the start of every five-second cycle. Timing comes from the
 * Hero Motion Lab settings that the team chose.
 *
 * Every move is opacity, translate, or scale on the compositor. The parts start hidden through their
 * motion-pending: utilities and play once the hero is on screen (modules/motion/once.js).
 */
import {EASING_CURVES} from '../../modules/motion/easing.js';
import {playOnce} from '../../modules/motion/once.js';
import {holdLoopsOffScreen} from '../../modules/motion/onScreen.js';

const SELECTOR = Object.freeze({
    root: '.js-homeHero',
    quote: '.js-homeHeroQuote',
    barItem: '.js-homeHeroBarItem',
    check: '.js-homeHeroCheck',
    cta: '.js-homeHeroCta',
    arrow: '[data-icon-name="chevron-right"]',
});

/** Milliseconds. A part list starts at `start` and each next part follows `step` later. */
const TIMING = Object.freeze({
    quote: {start: 150, step: 100, duration: 600},
    /* The proof bar below md, where the quotes column is hidden. */
    barItem: {start: 200, step: 90, duration: 600},
    check: {start: 260, step: 130, duration: 560},
});

/** Milliseconds. The nudge starts this long after the entrance ends, then repeats every cycle. */
const NUDGE = Object.freeze({after: 600, cycle: 5000});

/** Pixels a quote rises. */
const RISE = 8;

const EASE = Object.freeze({
    rise: EASING_CURVES['ease-out-cubic'],
    tick: EASING_CURVES.spring,
    nudge: EASING_CURVES['ease-in-out'],
});

/** Hold the first keyframe through the delay; the natural styles take over at the end. */
const FILL = 'backwards';

/**
 * @param {HTMLElement} root
 * @param {keyof typeof TIMING} part
 * @returns {Array<{element: HTMLElement, timing: {delay: number, duration: number}}>}
 */
function cued(root, part) {
    const {start, step, duration} = TIMING[part];

    return [...root.querySelectorAll(SELECTOR[part])].map((element, index) => ({
        element: /** @type {HTMLElement} */ (element),
        timing: {delay: start + step * index, duration},
    }));
}

/**
 * Two small pushes at the start of every cycle. It adds to the arrow's own translate, so the
 * button's hover shift still works under it.
 * @param {HTMLElement} cta
 * @param {number} delay
 */
function nudgeArrow(cta, delay) {
    const arrow = cta.querySelector(SELECTOR.arrow);
    if (!(arrow instanceof SVGElement)) {
        throw new Error('The home hero button needs its chevron-right arrow.');
    }
    arrow.animate(
        [{translate: '0 0'}, {translate: '4px 0', offset: 0.04}, {translate: '0 0', offset: 0.08}, {translate: '3px 0', offset: 0.12}, {translate: '0 0', offset: 0.16}, {translate: '0 0'}],
        {delay, duration: NUDGE.cycle, iterations: Infinity, easing: EASE.nudge, composite: 'add'}
    );
    holdLoopsOffScreen(cta);
}

/**
 * @param {HTMLElement} root
 * @param {import('../../modules/motion/run.js').Run} run
 */
function start(root, run) {
    let end = 0;
    /**
     * @param {HTMLElement} element
     * @param {Array<Record<string, string|number>>} keyframes
     * @param {{delay: number, duration: number}} timing
     * @param {string} easing
     */
    const play = (element, keyframes, timing, easing) => {
        run.add(element.animate(keyframes, {...timing, easing, fill: FILL}));
        end = Math.max(end, timing.delay + timing.duration);
    };
    for (const part of /** @type {const} */ (['quote', 'barItem'])) {
        for (const {element, timing} of cued(root, part)) {
            play(element, [{opacity: 0, translate: `0 ${RISE}px`}, {opacity: 1, translate: '0 0'}], timing, EASE.rise);
        }
    }
    for (const {element, timing} of cued(root, 'check')) {
        play(element, [{opacity: 0, scale: 0}, {opacity: 1, scale: 1}], timing, EASE.tick);
    }
    const cta = root.querySelector(SELECTOR.cta);
    if (!(cta instanceof HTMLElement)) {
        throw new Error('The home hero entrance needs the button.');
    }
    nudgeArrow(cta, end + NUDGE.after);
}

/**
 * Play the entrance of the home hero.
 */
export function playHeroIntro() {
    const root = document.querySelector(SELECTOR.root);
    if (!(root instanceof HTMLElement)) {
        throw new Error('The home hero entrance needs the hero root.');
    }
    playOnce(root, (run) => start(root, run));
}
